import {connect, useDispatch} from "react-redux";
import {
  getUser,
  isAuthenticated,
  isLoading,
} from "../../../store/selectors/AuthSelectors";
import {Formik} from "formik";
import {useEffect, useState} from "react";
import {useMe} from "../../../hooks/useGetUser";
import Loader from "../shared/Loader";
import * as yup from "yup";
import {
  updateProfileAction,
} from "../../../store/actions/AuthActions";
import {useNavigate} from "react-router-dom";
import {useQueryClient} from "@tanstack/react-query";
import {geocodeByPlaceId} from "react-google-places-autocomplete";
import GooglePlacesAutocomplete from "../shared/SafeGooglePlacesAutocomplete";
import {VerifTelModal} from "./VerifTelModal";
import {formatPhoneNumberIntl} from "react-phone-number-input";
import googleMapsApiKey from "../../../config/googleMaps";
import {
  clearProfileAddressFields,
  normalizeProfileAddressValues,
} from "../../../utils/profileAddress";

const formatDateForInput = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const ProfileForm = ({profileFile, setProfileFile, isLoading}) => {
  const schema = yup.object().shape({
    firstName: yup.string().required("Champ obligatoire"),
    birthDate: yup.date().required("Champ obligatoire"),
    lastName: yup.string().required("Champ obligatoire"),
    gender: yup.string().required("Champ obligatoire"),
    email: yup
      .string()
      .required("Champ obligatoire")
      .email("L'e-mail doit être un e-mail valide"),
    phone: yup
      .string()
      .transform((value) => String(value ?? "").trim())
      .required("Champ obligatoire")
      .matches(
        /^\+\d{1,3}(?:[\s.-]*\d){4,14}$/,
        "Le numéro de téléphone doit être au format +[indicatif] [numéro]",
      )
      .test(
        "phone-length",
        "Le numéro de téléphone doit contenir entre 8 et 15 chiffres",
        (value) => {
          const digitCount = String(value || "").replace(/\D/g, "").length;
          return digitCount >= 8 && digitCount <= 15;
        },
      ),
    address: yup
      .string()
      .transform((value) => value ?? "")
      .nullable()
      .test(
        "address-length",
        "L'adresse doit contenir au moins 5 caractères",
        (value) => !value || value.length >= 5,
      ),
    city: yup
      .string()
      .nullable()
      .when("address", {
        is: (val) => val && val.length > 0, // Check if address is not empty
        then: (schema) =>
          schema
            .required("Veuillez entrer votre adresse complète")
            .min(2, "La ville doit contenir au moins 2 caractères"),
        otherwise: (schema) => schema.notRequired(),
      }),
    street: yup.string().nullable().notRequired(),
    streetNumber: yup.string().nullable().notRequired(),

    zipCode: yup
      .string()
      .nullable()
      .when("address", {
        is: (val) => val && val.length > 0, // Check if address is not empty
        then: (schema) =>
          schema
            .required("Veuillez entrer votre adresse complète")
            .matches(
              /^\d{5}$/,
              "Le code postal doit contenir exactement 5 chiffres"
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [openVerifModal, setOpenVerifModal] = useState(false);

  const {data, isLoading: meIsLoading} = useMe();
  const [selectedAddress, setSelectedAddress] = useState("");
  // const [profileFile, setProfileFile] = useState(null);

  const [Pro, setPro] = useState(false);
  useEffect(() => {
    if (data) {
      const {companyName, taxRegistrationNumber} = data?.Technician;
      if (companyName || taxRegistrationNumber) {
        setPro(true);
      }
    }
  }, [data]);
  if (meIsLoading || isLoading) {
    return <Loader />;
  }
  if (data) {
  }
  const SubmitUpdate = (values, profileImg) => {
    const normalizedValues = {
      ...normalizeProfileAddressValues(values),
      phone: String(values.phone || "").replace(/[\s.-]/g, ""),
    };

    if (normalizedValues.email !== email) {
      normalizedValues.verifiedEmail = false;
    }

    return dispatch(
      updateProfileAction(
        {
          body: {...normalizedValues, profileImg},
          id: id,
          technicianID: data?.Technician?.id,
        },
        navigate,
        queryClient
      )
    );
  };

  const handlePlaceSelect = async (address, setFieldValue, setFieldTouched) => {
    if (!address) {
      await clearProfileAddressFields(setFieldValue);
      await setFieldTouched("address", true, false);
      setSelectedAddress("");
      return;
    }

    const [place] = await geocodeByPlaceId(address.value.place_id);

    const {long_name: postalCode = ""} =
      place.address_components.find((c) => c.types.includes("postal_code")) ||
      {};
    const {long_name: ville = ""} =
      place.address_components.find((c) => c.types.includes("locality")) || {};
    const street =
      place.address_components.find((c) => c.types.includes("route"))
        ?.long_name || "";
    const streetNumber =
      place.address_components.find((c) => c.types.includes("street_number"))
        ?.long_name || "";

    await Promise.all([
      setFieldValue("streetNumber", streetNumber, false),
      setFieldValue("street", street, false),
      setFieldValue("address", address.label, false),
      setFieldValue("zipCode", postalCode, false),
      setFieldValue("city", ville, false),
    ]);
    await setFieldTouched("address", true, true);
    setSelectedAddress(address);
  };

  const {
    firstName,
    lastName,
    email,
    gender,
    birthDate,
    phone,
    id,
    zipCode,
    city,
    address,
  } = data;
  const {companyName, taxRegistrationNumber} = data?.Technician;

  const birthDate2 = formatDateForInput(birthDate);

  return (
    <div className="!pt-3 py-5 w-full">
      <div className="settings-form">
        <Formik
          enableReinitialize
          initialValues={{
            firstName,
            lastName,
            email,
            gender: ["male", "female", "other"].includes(gender) ? gender : "",
            birthDate: birthDate2,
            phone: formatPhoneNumberIntl(phone || "") || phone || "",
            companyName,
            taxRegistrationNumber,
            zipCode: zipCode ?? "",
            city: city ?? "",
            address: address ?? "",
            streetNumber: "",
            street: "",
          }}
          validationSchema={schema}
          onSubmit={async (values, {setSubmitting}) => {
            const submittedPhone = String(values.phone || "").replace(/[\s.-]/g, "");
            const currentPhone = String(phone || "").replace(/[\s.-]/g, "");
            if (submittedPhone !== currentPhone) {
              setOpenVerifModal(true);
              setSubmitting(false);
            } else {
              try {
                await SubmitUpdate(values, profileFile);
                setProfileFile(null);
                setSelectedAddress("");
              } catch {
                // The update action already displays the API error.
              } finally {
                setSubmitting(false);
              }
            }
          }}
        >
          {({
            values,
            errors,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            resetForm,
            setFieldValue,
            setFieldTouched,
            touched,
            dirty,
          }) => (
            <form onSubmit={handleSubmit}>
              <VerifTelModal
                email={values.email}
                handleSubmit={async () => {
                  try {
                    await SubmitUpdate(values, profileFile);
                    setProfileFile(null);
                    setSelectedAddress("");
                    setOpenVerifModal(false);
                  } catch {
                    // The update action already displays the API error.
                  }
                }}
                phone={values.phone}
                setShow={setOpenVerifModal}
                show={openVerifModal}
              />

              <div className="row !m-0">
                {/* <div className="w-full flex justify-start items-center mb-3">
                  <InputPhoto
                    value={values.imageUri || ""}
                    onChange={(e) => handlePhotoChange(e, setFieldValue)}
                    multiple={false}
                    accept={"image/*"}
                  />
                  {errors.profileFile && (
                    <div
                      className="invalid-feedback animated fadeInUp"
                      style={{display: "block"}}
                    >
                      {errors.profileFile}
                    </div>
                  )}
                </div> */}
                <div className="form-group mb-3 col-md-6">
                  <label className="form-label" htmlFor="provider-lastName">Nom</label>
                  <input
                    id="provider-lastName"
                    type="text"
                    className="form-control"
                    name="lastName"
                    onChange={handleChange}
                    onBlur={(event) => {
                      handleBlur(event);
                      const formatted = formatPhoneNumberIntl(event.target.value);
                      if (formatted) setFieldValue("phone", formatted);
                    }}
                    value={values.lastName}
                  />
                  {errors.lastName && (
                    <div
                      className="invalid-feedback animated fadeInUp"
                      style={{display: "block"}}
                    >
                      {errors.lastName}
                    </div>
                  )}
                </div>
                <div className="form-group mb-3 col-md-6">
                  <label className="form-label" htmlFor="provider-firstName">Prénom</label>
                  <input
                    id="provider-firstName"
                    type="text"
                    className="form-control"
                    name="firstName"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.firstName}
                  />
                  {errors.firstName && (
                    <div
                      className="invalid-feedback animated fadeInUp"
                      style={{display: "block"}}
                    >
                      {errors.firstName}
                    </div>
                  )}
                </div>
                <div className="form-group mb-3 col-md-6">
                  <label className="form-label" htmlFor="provider-email">Email</label>
                  <input
                    id="provider-email"
                    type="email"
                    name="email"
                    className="form-control"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                  />
                  {errors.email && (
                    <div
                      className="invalid-feedback animated fadeInUp"
                      style={{display: "block"}}
                    >
                      {errors.email}
                    </div>
                  )}
                </div>
                <div className="form-group mb-3 col-md-6">
                  <label className="form-label" htmlFor="provider-phone">Numéro de téléphone</label>
                  <input
                    id="provider-phone"
                    type="text"
                    className="form-control !text-[#333333]"
                    name="phone"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.phone}
                  />
                  {errors.phone && (
                    <div
                      className="invalid-feedback animated fadeInUp"
                      style={{display: "block"}}
                    >
                      {errors.phone}
                    </div>
                  )}
                </div>
                <div className="form-group mb-3 col-md-6">
                  <label className="form-label" htmlFor="provider-birthDate">Date de naissance</label>
                  <input
                    id="provider-birthDate"
                    type="date"
                    className="form-control"
                    name="birthDate"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.birthDate}
                  />
                  {errors.birthDate && (
                    <div
                      className="invalid-feedback animated fadeInUp"
                      style={{display: "block"}}
                    >
                      {errors.birthDate}
                    </div>
                  )}
                </div>
                <div className="form-group mb-3 col-md-6">
                  <label className="form-label" htmlFor="provider-gender">Genre</label>

                  <select
                    id="provider-gender"
                    className="form-control"
                    name="gender"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.gender}
                  >
                    <option value={"female"}>Femme</option>
                    <option value={"male"}>Homme</option>
                    <option value={"other"}>Autre</option>
                  </select>
                  {errors.gender && (
                    <div
                      className="invalid-feedback animated fadeInUp"
                      style={{display: "block"}}
                    >
                      {errors.gender}
                    </div>
                  )}
                </div>

                <div className="form-group mb-3 col-md-12">
                  <label className="form-label" htmlFor="provider-address">Adresse</label>
                  <GooglePlacesAutocomplete
                    autocompletionRequest={{
                      componentRestrictions: {country: ["fr"]},
                      types: ["address"],
                    }}
                    apiKey={googleMapsApiKey}
                    selectProps={{
                      inputId: "provider-address",
                      noOptionsMessage: () => "Aucune option disponible",

                      value:
                        selectedAddress ||
                        (values.address
                          ? {
                              label: values.address,
                              value: {place_id: null},
                            }
                          : null),
                      isClearable: true,
                      backspaceRemovesValue: true,
                      escapeClearsValue: true,
                      onChange: (address) =>
                        handlePlaceSelect(
                          address,
                          setFieldValue,
                          setFieldTouched
                        ),
                      placeholder: "Recherchez une adresse...",
                      styles: {
                        dropdownIndicator: (base) => ({
                          ...base,
                          display: "none", // Hides the dropdown icon
                        }),
                        indicatorSeparator: (base) => ({
                          ...base,
                          display: "none", // Hides the separator line
                        }),
                        control: (base) => ({
                          ...base,
                          borderColor: "#eee", // Matches the other inputs' border color
                          borderWidth: "1px",
                          borderRadius: "0.375rem", // Rounded corners like your other inputs
                          padding: "0.375rem 0.75rem", // Add padding to match the input
                          boxShadow: "none",
                        }),
                        input: (base) => ({
                          ...base,
                          fontSize: "0.875rem", // Matches your other inputs' font size
                          color: "#374151", // Text color
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "#9ca3af", // Placeholder color
                          fontSize: "0.875rem",
                        }),
                      },
                    }}
                  />
                  {touched.address &&
                    (errors.address ||
                      errors.zipCode ||
                      errors.city ||
                      errors.street ||
                      errors.streetNumber) && (
                      <div
                        className="invalid-feedback animated fadeInUp"
                        style={{display: "block"}}
                      >
                        {errors.address ||
                          errors.zipCode ||
                          errors.city ||
                          errors.street ||
                          errors.streetNumber}
                      </div>
                    )}
                </div>
                <div className="w-full">
                  <div className="form-group mb-3 col-md-6">
                    <label className="form-label ">Vous êtes ? </label>
                    <div className="flex w-full">
                      <label className="cursor-pointer w-1/2">
                        <input
                          type="radio"
                          name="role"
                          value="2"
                          className="hidden"
                          checked={!Pro}
                          onChange={() => {
                            setPro(false);
                            setFieldValue("companyName", "");
                            setFieldValue("taxRegistrationNumber", "");
                          }}
                        />
                        <div
                          className={`w-full py-2 text-center rounded-s-lg border-t-2 border-b-2 border-l-2 !border-r-0 ${
                            !Pro
                              ? "bg-[#FF6600] text-white border-[#FF6600]"
                              : "bg-white text-gray-700 border-gray-300"
                          }`}
                        >
                          Particulier
                        </div>
                      </label>
                      <label className="cursor-pointer w-1/2">
                        <input
                          type="radio"
                          name="role"
                          value="3"
                          className="hidden"
                          checked={Pro}
                          onChange={() => setPro(true)}
                        />
                        <div
                          className={`w-full py-2 text-center rounded-e-lg border-t-2 border-b-2 border-l-0 border-r-2 ${
                            Pro
                              ? "bg-[#FF6600] text-white border-[#FF6600]"
                              : "bg-white text-gray-700 border-gray-300"
                          }`}
                        >
                          Professionnel
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                {Pro && (
                  <>
                    <div className="form-group mb-3 col-md-6">
                      <label className="form-label" htmlFor="provider-companyName">Raison Sociale</label>
                      <input
                        id="provider-companyName"
                        type="text"
                        name="companyName"
                        className="form-control"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.companyName}
                      />
                    </div>
                    <div className="form-group mb-3 col-md-6">
                      <label className="form-label" htmlFor="provider-taxRegistrationNumber">Matricule Fiscal</label>
                      <input
                        id="provider-taxRegistrationNumber"
                        type="text"
                        name="taxRegistrationNumber"
                        className="form-control"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.taxRegistrationNumber}
                      />
                    </div>
                  </>
                )}
              </div>
              {/* <div className="flex justify-center gap-1 lg:gap-3 mt-4 lg:justify-end flex-wrap-reverse px-[15px] ">
                <button
                  className="btn border-none underline underline-offset-2 min-w-[200px] w-[200px] !rounded-lg"
                  type="reset"
                  onClick={() => {
                    resetForm();
                    setSelectedAddress("");
                    setProfileFile(null);
                  }}
                  disabled={isSubmitting}
                >
                  Annuler
                </button>
                <button
                  className="btn !bg-[#FF6600] text-white min-w-[300px] w-1/5 !rounded-lg"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Enregistrement..." : "Enregistrer"}{" "}
                </button>
              </div> */}
              <div className="flex justify-center gap-1 lg:gap-3 mt-4 lg:justify-end flex-wrap-reverse px-[15px] ">
                <button
                  className="btn !min-w-[250px] !rounded-lg bg-white !text-[#0D44B1] !border-[#0D44B1] !border border-solid"
                  type="reset"
                  onClick={() => {
                    resetForm();
                    setSelectedAddress("");
                    setProfileFile(null);
                  }}
                  disabled={isSubmitting}
                >
                  Annuler
                </button>
                <button
                  className="btn !min-w-[250px] !rounded-lg bg-gradient-to-r from-[#0022AA] to-[#1A64B9] text-white"
                  type="submit"
                  disabled={isSubmitting || (!dirty && !profileFile)}
                >
                  {isSubmitting ? "Enregistrement..." : "Enregistrer"}{" "}
                </button>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </div>
  );
};
const mapStateToProps = (state) => {
  return {
    isAuthenticated: isAuthenticated(state),
    isLoading: isLoading(state),
    user: getUser(state),
  };
};
export default connect(mapStateToProps)(ProfileForm);
